import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import SignaturePad from 'signature_pad';

export interface NgSignaturePadOptions {
  canvasWidth?: number;
  canvasHeight?: number;
  backgroundColor?: string;
  penColor?: string;
  minWidth?: number;
  maxWidth?: number;
  throttle?: number;
  velocityFilterWeight?: number;
  dotSize?: number;
}

@Component({
  selector: 'signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrl: './signature-pad.component.css',
  standalone: false,
})
export class SignaturePadComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('canvasRef', { static: true })
  private canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() options: NgSignaturePadOptions = {};

  private signaturePadInstance: SignaturePad | null = null;
  private resizeObserver: ResizeObserver | null = null;

  ngAfterViewInit(): void {
    this.initializePad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.signaturePadInstance || !changes['options']) {
      return;
    }
    this.applyOptions();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  clear(): void {
    this.signaturePadInstance?.clear();
  }

  isEmpty(): boolean {
    return this.signaturePadInstance ? this.signaturePadInstance.isEmpty() : true;
  }

  toDataURL(type?: string, encoderOptions?: number): string {
    if (!this.signaturePadInstance) {
      return 'data:,';
    }
    return this.signaturePadInstance.toDataURL(type, encoderOptions);
  }

  fromDataURL(dataUrl: string, options?: { ratio?: number; width?: number; height?: number; xOffset?: number; yOffset?: number }): void {
    this.signaturePadInstance?.fromDataURL(dataUrl, options);
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvasRef.nativeElement;
  }

  private initializePad(): void {
    const canvas = this.canvasRef.nativeElement;
    this.signaturePadInstance = new SignaturePad(canvas);
    this.applyOptions();
    this.observeResize();
  }

  private applyOptions(): void {
    const canvas = this.canvasRef.nativeElement;

    if (this.options.canvasWidth && this.options.canvasWidth > 0) {
      canvas.style.width = `min(100%, ${this.options.canvasWidth}px)`;
    } else {
      canvas.style.width = '100%';
    }

    if (this.options.canvasHeight && this.options.canvasHeight > 0) {
      canvas.style.height = `${this.options.canvasHeight}px`;
    } else {
      canvas.style.height = '150px';
    }

    this.resizeCanvas();

    if (!this.signaturePadInstance) {
      return;
    }

    if (this.options.backgroundColor != null) {
      this.signaturePadInstance.backgroundColor = this.options.backgroundColor;
    }
    if (this.options.penColor != null) {
      this.signaturePadInstance.penColor = this.options.penColor;
    }
    if (this.options.minWidth != null) {
      this.signaturePadInstance.minWidth = this.options.minWidth;
    }
    if (this.options.maxWidth != null) {
      this.signaturePadInstance.maxWidth = this.options.maxWidth;
    }
    if (this.options.throttle != null) {
      this.signaturePadInstance.throttle = this.options.throttle;
    }
    if (this.options.velocityFilterWeight != null) {
      this.signaturePadInstance.velocityFilterWeight = this.options.velocityFilterWeight;
    }
    if (this.options.dotSize != null) {
      this.signaturePadInstance.dotSize = this.options.dotSize;
    }
  }

  private observeResize(): void {
    const canvas = this.canvasRef.nativeElement;
    const container = canvas.parentElement;

    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
    this.resizeObserver.observe(container);
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = Math.max(Math.floor(rect.width * ratio), 1);
    const height = Math.max(Math.floor(rect.height * ratio), 1);

    if (canvas.width === width && canvas.height === height) {
      return;
    }

    const existingStrokes = this.signaturePadInstance?.toData();
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context?.setTransform(ratio, 0, 0, ratio, 0, 0);
    if (existingStrokes && existingStrokes.length > 0) {
      this.signaturePadInstance?.fromData(existingStrokes);
      return;
    }
    this.signaturePadInstance?.clear();
  }
}
